import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { LocalizedPageTitle, LocalizedParagraph, OverlayedLoader } from '@mews-commander/ui';
import { LocalizedText, lt, showErrorAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Pictogram, Stack } from '@optimus-web/core';
import { NewPublicFormWrapper } from '../../style';
import { useSignIn } from '../useSignIn';

export interface AuthorizeDeviceProps {
    signInToken: string;
    authorizationToken: string;
}

export function AuthorizeDevice({ signInToken, authorizationToken }: AuthorizeDeviceProps) {
    const navigate = useNavigate();
    const {
        isPending,
        data,
        mutate: authorizeDevice,
    } = useRepositoryMutation('generalUsers', 'authorizeDevice', {
        meta: {
            disableDefaultErrorHandler: true,
        },
        onError: async () => {
            await navigate({ to: '/Home/SignIn' });
            showErrorAlert({
                description: lt('HardDeviceApprovalAcceptExpiredLinkError'),
                title: lt('LinkExpired'),
            });
        },
    });
    const { signIn, isLoginPending: isLoadingDashboard } = useSignIn();

    useEffect(() => {
        authorizeDevice({ signInToken, authorizationToken });
    }, [signInToken, authorizationToken, authorizeDevice]);

    return (
        <NewPublicFormWrapper>
            <OverlayedLoader show={isPending} />
            {data && (
                <Stack spacing="500" vertical>
                    <Pictogram value="success" size="large" />
                    <Stack spacing="300" vertical>
                        <Stack spacing="100" vertical>
                            <LocalizedPageTitle textKey={TextKey.HardDeviceApprovalAcceptTitle} />
                            <LocalizedParagraph textKey={TextKey.HardDeviceApprovalAcceptDescription} />
                        </Stack>
                        {data.isOwnDevice && (
                            <Stack>
                                <Button
                                    type="button"
                                    variant="tertiary"
                                    size="medium"
                                    onClick={signIn}
                                    loading={isLoadingDashboard}
                                >
                                    <LocalizedText textKey={TextKey.Continue} />
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            )}
        </NewPublicFormWrapper>
    );
}
